import { ensureSafeComponent } from '@embroider/util';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Task } from 'ember-concurrency';
import { eq } from 'ember-truth-helpers';
import PropagableSettingsChangedModalChangesConfirm from 'garaje/components/propagable-settings-changed/modal/changes-confirm';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import type Flow from 'garaje/models/flow';
import type GlobalSettingBatch from 'garaje/models/global-setting-batch';
import type { AllPropagableModel } from 'garaje/models/global-setting-batch';
import type Location from 'garaje/models/location';
import LiquidIf from 'liquid-fire/components/liquid-if';

interface PropagableSettingsChangedModalSignature {
  Args: {
    forType: string;
    globalSettingBatch: GlobalSettingBatch;
    overwriteTask: Task<void, []>;
    selectedChildren: AllPropagableModel[];
    updateSelectedChildren: (children: Location[] | Flow[]) => void;
    close: () => void;
  };
}

export default class PropagableSettingsChangedModal extends Component<PropagableSettingsChangedModalSignature> {
  @tracked currentStep = 'searcher';

  get forType(): string {
    return this.args.forType ?? 'locations';
  }

  get searcherComponentName(): string {
    return `propagable-settings-changed/modal/${this.forType}-searcher`;
  }

  nextToConfirm = (): void => {
    this.currentStep = 'confirm';
  };

  backToSearcher = (): void => {
    this.currentStep = 'searcher';
  };

  <template>
    <ModalDialog
      @containerClass="ember-modal-dialog--propagable-modal"
      @clickOutsideToClose={{false}}
      @targetAttachment="center"
      @translucentOverlay={{true}}
    >
      <LiquidIf @predicate={{eq this.currentStep "searcher"}}>
        <:default>
          {{#let (component (ensureSafeComponent this.searcherComponentName this)) as |SearcherComponent|}}
            <SearcherComponent
              @selectedChildren={{@selectedChildren}}
              @globalSettingBatch={{@globalSettingBatch}}
              @close={{@close}}
              @nextToConfirm={{this.nextToConfirm}}
              @updateChildren={{@updateSelectedChildren}}
            />
          {{/let}}
        </:default>
        <:else>
          <PropagableSettingsChangedModalChangesConfirm
            @selectedChildren={{@selectedChildren}}
            @globalSettingBatch={{@globalSettingBatch}}
            @forType={{this.forType}}
            @close={{@close}}
            @backToSearcher={{this.backToSearcher}}
            @overwriteTask={{@overwriteTask}}
          />
        </:else>
      </LiquidIf>
    </ModalDialog>
  </template>
}
